import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { SpendThreshold, Usage } from '../lib/billing';
import { progressPct } from '../lib/progress';
import { money } from '../lib/time';
import { SpendCapEditor } from './SpendCapEditor';
import { StatusPill, type StatusTone } from './StatusPill';

// Usage & spend panel body (mock Billing()): the mono spend-vs-cap headline with
// the threshold pill, the `.progress` gauge and the cap editor, then the
// handled-units fact. Presenter — Billing passes the rollup + a cap-saved
// refetch. The cap editor is an executable `write:billing` action (PATCH
// /v1/billing/cap), so its gate is the CALLER's settled verdict: `maySetCap`
// carries it (Billing passes its `useScopeVerdict` accessor) and hides the
// editor on 'denied' and 'unsettled' while the spend facts stay; an absent
// prop renders — standalone stories carry no verdict to wait on. Waived
// accounts (personal / OSS-verified) also get a Waived badge and
// note, but keep the spend block and cap editor: the waiver is not a spend
// exemption, agent-run gates a waived account on its own cap too (#2057).

// Threshold → note (ok has none) + pill tone; the gauge fill colours the states.
const THRESHOLD_NOTE: Record<Exclude<SpendThreshold, 'ok'>, string> = {
  warn_50: 'billing.threshold_warn_50',
  warn_80: 'billing.threshold_warn_80',
  capped: 'billing.threshold_capped',
};
const THRESHOLD_TONE: Record<SpendThreshold, StatusTone> = {
  ok: 'success',
  warn_50: 'warning',
  warn_80: 'warning',
  capped: 'danger',
};
const THRESHOLD_LABEL: Record<SpendThreshold, string> = {
  ok: 'billing.cap_state_ok',
  warn_50: 'billing.cap_state_warn_50',
  warn_80: 'billing.cap_state_warn_80',
  capped: 'billing.cap_state_capped',
};

/**
 * Usage & spend panel body — the gate contract is in the header above.
 */
export function BillingUsage(props: {
  usage: Usage;
  token: string | null;
  /** The caller's settled `write:billing` verdict (scope-gate); absent ⇒ render. */
  maySetCap?: () => boolean;
  onCapSaved: () => void;
  /** Surface a save outcome through the page's toast (#924). */
  onNotice?: (message: string, tone: 'success' | 'error') => void;
}): JSX.Element {
  // #2057: the server reports the spend directly. It used to be re-derived as
  // `used_fraction × spend_cap_usd`, which is 0 for an account with no cap —
  // and `spend_cap_usd` is now null in exactly that case.
  const spent = (): number => props.usage.spend_usd;
  const cap = (): number | null => props.usage.spend_cap_usd;

  return (
    <>
      <div class="billing-spend">
        <div>
          <p class="billing-spend__label">{t('billing.spend_cap')}</p>
          <p class="billing-spend__value">
            {money(spent())}{' '}
            <Show when={cap() !== null}>
              <span class="billing-spend__cap">/ {money(cap() ?? 0)}</span>
            </Show>
          </p>
        </div>
        <StatusPill tone={THRESHOLD_TONE[props.usage.threshold]}>
          {t(THRESHOLD_LABEL[props.usage.threshold])}
        </StatusPill>
      </div>
      <div
        class="progress billing-spend__gauge"
        data-threshold={props.usage.threshold}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={Math.min(1, Math.max(0, props.usage.used_fraction))}
        aria-label={t('billing.spend_cap')}
      >
        <div
          class="progress__indicator"
          style={{ width: `${progressPct(props.usage.used_fraction, 1)}%` }}
        />
      </div>
      {/* The ceiling for the runs the platform could not price is an upper bound,
          not money spent, so it rides on its own line instead of in the figure
          above. The gauge and the pill include it: the cap refuses on the sum. */}
      <Show when={props.usage.spend_estimate?.usd ? props.usage.spend_estimate : undefined}>
        {(estimate) => (
          <p class="billing-spend__note" data-estimate>
            {tPlural('billing.spend_estimate', estimate().runs, { amount: money(estimate().usd) })}
          </p>
        )}
      </Show>
      <Show when={props.usage.threshold !== 'ok'}>
        <p class="billing-spend__note" data-threshold={props.usage.threshold} role="status">
          {t(THRESHOLD_NOTE[props.usage.threshold as Exclude<SpendThreshold, 'ok'>])}
        </p>
      </Show>
      <Show when={props.maySetCap?.() ?? true}>
        <SpendCapEditor
          current={props.usage.spend_cap_usd}
          token={props.token}
          onSaved={props.onCapSaved}
          {...(props.onNotice ? { onNotice: props.onNotice } : {})}
        />
      </Show>

      <dl class="facts billing-usage__stats">
        <div>
          <dt>{t('billing.handled_units')}</dt>
          <dd class="facts__num">{props.usage.handled_units}</dd>
        </div>
      </dl>

      <Show when={props.usage.waived}>
        <p class="billing-usage__waived">
          <StatusPill>{t('billing.waived')}</StatusPill> {t('billing.waived_note')}
        </p>
      </Show>
    </>
  );
}
